'use client'

// NavScrollController — the header's scroll-pin island (#118 server-chrome, Step
// 3). It owns the three DOM refs and the scroll handler the old class <Header>
// owned, and renders the header's OUTER structure: the wrapper, the empty anchor
// div (whose offsetTop is the pin threshold), the live header div, and the
// off-screen fixed-scroll clone div.
//
// The nav CONTENT of each copy is server-eligible and arrives as the `liveNav` /
// `cloneNav` props — server-rendered JSX (a <HeaderNavServer/>) passed from the
// SiteHeader shell (RSC-serialisable; NOT a function). This island therefore owns
// ONLY the scroll behaviour + structure; it drills the store nothing.
//
// SCROLL BEHAVIOUR (VERBATIM from the class handleScroll): once the window scrolls
// past the anchor's offsetTop, the live header becomes position:fixed and the
// clone is revealed (display:block) as a layout spacer; on scroll back it reverts.
// Every tick writes navbarElementProps via saveNavbarElementProps (through the
// narrow useNavbarProps hook, now backed by the Zustand ui store — Phase C2 part
// 1), so the SecondaryNavbar keeps pinning under the header.

import React, { Component } from 'react'
import elementStyles from './elements.module.css'
import { useNavbarProps } from './useNavbarProps'

class NavScrollControllerInner extends Component {
  constructor(props) {
    super(props)
    this.navigationAnchorRef = React.createRef()
    this.navigationRef = React.createRef()
    this.navigationCloneRef = React.createRef()
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  handleScroll = (event) => {
    const windowTop = event.target.scrollingElement.scrollTop
    const navigationTop = this.navigationAnchorRef.current.offsetTop

    // achieve smooth transition between fixed and other positions, read more here: https://noto.li/7IyTHj
    if (windowTop > navigationTop) {
      this.navigationRef.current.style.position = 'fixed'
      this.navigationCloneRef.current.style.display = 'block'
    } else {
      this.navigationRef.current.style.position = 'relative'
      this.navigationCloneRef.current.style.display = 'none'
    }

    this.props.saveNavbarElementProps({
      offsetTop: navigationTop,
      offsetHeight: this.navigationRef.current.offsetHeight
    })
  }

  render() {
    const { liveNav, cloneNav } = this.props

    return (
      // Landmark (Audit C M1): the site header root is a <header> so it exposes
      // the implicit ARIA `banner` landmark (it is a direct child of the chrome
      // container, not nested in another landmark). Class unchanged — CSS-module
      // selectors are tag-agnostic, so this is a semantic-only change (no pixels).
      <header className={elementStyles.wrapper}>
        <div ref={this.navigationAnchorRef}/>
        <div className={elementStyles.headerBase} ref={this.navigationRef}>
          {liveNav}
        </div>
        <div className={`${elementStyles.headerBase} ${elementStyles.headerCloneWrapper}`} ref={this.navigationCloneRef}>
          {cloneNav}
        </div>
      </header>
    )
  }
}

// Thin function wrapper so the class can consume the narrow selector hook (the
// useActions-not-prop change, risk #2). saveNavbarElementProps is drilled in as a
// prop to the class exactly as `actions` used to be — but now sourced from the
// island's OWN useActions(), not from Layout.
function NavScrollController({ liveNav, cloneNav }) {
  const { saveNavbarElementProps } = useNavbarProps()
  return (
    <NavScrollControllerInner
      liveNav={liveNav}
      cloneNav={cloneNav}
      saveNavbarElementProps={saveNavbarElementProps}
    />
  )
}

export default NavScrollController
